import { AssemblerContext } from '@/types/environment';

type Origin =
  | 'GAMES'
  | 'LIVE_CASINO'
  | 'GCC'
  | 'CASINO'
  | 'SCRATCHCARDS'
  | 'VEGAS'
  | 'QUICKSILVER'
  | 'BINGO';

type FeatureStateType = 'enabled' | 'disabled' | 'hidden';

interface Route {
  type: 'static' | 'dynamic';
  path: string;
  params?: { [key: string]: string };
  pageId: string;
  id: string;
  slug?: string;
  socket: string;
  layout: string;
}

interface PageRoute extends Route {
  subtype: 'standard';
}

interface MenuRoute extends Route {
  subtype: 'menu';
}

interface GameRoute extends Route {
  subtype: 'game-details';
}

interface PromotionsRoute extends Route {
  subtype: 'promotions';
}

interface SearchRoute extends Route {
  subtype: 'search';
}

interface GameLaunchRoute extends Route {
  subtype: 'game_launch';
}

interface StaticFeatures {
  tracking: {
    mode: FeatureStateType;
    delayed: boolean;
    appIds: {
      android: string | null;
      ios: number | null;
    };
    appName: string | null;
    productContext: string | null;
    siteSection: string | null;
    pmtSource?: {
      mobile: string;
      tablet: string;
      desktop: string;
    };
    regSource?: {
      mobile: string;
      tablet: string;
      desktop: string;
    };
  };
  iFrame: {
    mode: FeatureStateType;
  };
  header: {
    variant: 'kraken';
  };
  session: {
    authToken: {
      variant: 'external' | 'internal';
    };
  };
  jackpotsV2: {
    allowedProviders?: string[];
    mode: FeatureStateType;
  };
  modalForMobile: {
    mode: FeatureStateType;
  };
  idlePopup: {
    checkboxEnabled?: boolean;
    bonusPermissionRequired?: boolean;
  };
}

// extends the static feature config with features controlled through CMS
interface Features extends StaticFeatures {
  gameDetails: {
    mode: FeatureStateType;
  };
  iFrame: {
    mode: FeatureStateType;
  };
  basecampBannersStaticRedirect: {
    mode: FeatureStateType;
    redirectUrl?: string;
  };
  livePersonChatListener: {
    mode: FeatureStateType;
    redirectUrl?: string;
    iosRedirectUrl?: string;
    name?: string;
  };
  strikeGoldWidget?: {
    defaultMarket: string;
  };
}

// describes the structure used in static config files that live in
// src/config/[variant]/[env].json, config files are checked during build time
export enum FunctionalitiesEnum {
  'promotions' = 'promotions',
  'gameDetails' = 'gameDetails',
  'gameLaunch' = 'gameLaunch',
  'search' = 'search',
  'video' = 'video',
}

// describes how static components should behave. By default responsive. When picked small / wide component will keep it's small / wide style on all screen sizes.
type StaticComponentsModes = 'default' | 'small' | 'wide';

type StaticComponents =
  | 'IdlePopup'
  | 'Navigation'
  | 'CookieBanner'
  | 'BottomNavigation'
  | 'Menu'
  | 'Modal'
  | 'GameDetailsModal'
  | 'Search'
  | 'Layout';

interface StaticConfig {
  jurisdiction: 'uk' | 'it' | 'es';
  profile: 'uk-web' | 'wh-mga' | 'it-web' | 'es-web' | 'wh-ca-on';
  defaultLocale: 'en-gb' | 'it-it' | 'es-es' | 'en-ca';
  urls: {
    app: string;
    bffBaseUrl: string;
  };
  staticComponents: Partial<
    Record<
      StaticComponents,
      {
        mode?: StaticComponentsModes;
        enabled?: boolean;
        desktopSidebarMode?: boolean;
      }
    >
  >;
  functionalities: Record<FunctionalitiesEnum, { path: string }>;
  special_routes?: Record<FunctionalitiesEnum, string[]>;
  launcher: {
    apiUrl: string;
    staticUrlParams: {
      [key: string]: string;
    };
  };
  gamesOrigins: Array<Origin>;
  history: {
    enabled: boolean;
  };
  disallowIndexing?: {
    enabled: boolean;
  };
  urlRedirection?: UrlRedirection[];
  features: StaticFeatures;
  algolia: {
    config: {
      appId: string;
      apiKey: string;
      index: string;
    };
  };
  intervals: {
    liveTableGamesCheck: number;
    recentlyPlayedGamesCheck: number;
    minDefaultRefetchTimeForBingo?: number;
    jackpots: {
      multiple: {
        section8: number;
        redtiger: number;
        blueprint: number;
      };
      games: number;
      tileTickerUpdateInterval: number;
    };
    jackpotBet: { feeds: number };
    idlePopup: {
      idleTime: number;
      lockTime: number;
    };
  };
}

// extends the static config with fields added in transformation scripts
// As there is also a merge with Assembly it should included into Config interface as-well
export interface Config extends StaticConfig, Partial<AssemblerContext> {
  headerTexts: {
    navigation: string;
    nonNavigation: string;
  };
  initLayout: {
    components: {
      [key: string]: any;
    };
  };
  features: Features;
  routes: Array<
    | PageRoute
    | MenuRoute
    | GameRoute
    | PromotionsRoute
    | SearchRoute
    | GameLaunchRoute
  >;
  seoGameMap?: {
    [key: string]: {
      title: string;
      description: string;
    };
  };
}

export interface UrlRedirection {
  type: 'hash';
  targetBaseUrl?: string;
  paths: Record<string, string>;
}
